import { ApiResponse, GlobalStore } from "@roc/feature-app-core";
import { makeObservable, observable, action, flow, computed } from "mobx";
import { CLOSING_INSTRUCTION_LETTER, CLOSING_PROTECTION_LETTER, PROOF_OF_RECEIPT, HUD, EIN_LETTER, TAX_CERTIFICATE, UW_HUD, HOA, PURCHASE_CONTRACT, WHOLESALER_CONTRACT, HUD_DOC, GC_LICENSE, BROKER_LICENSE, FINAL_TITLE_POLICY, RECORDED_MORTGAGE, RECORDED_ALR, ARTICLES_OF_INCORPORATION, BORROWING_CORP_STOCK_CERTIFICATE, BYLAWS, CERTIFICATE_OF_FORMATION, FILING_RECEIPT, OPERATING_AGREEMENT, CERTIFICATE_OF_GOOD_STANDING, CERTIFICATE_OF_GOOD_STANDING_BASE, CERTIFICATE_OF_EXISTENCE, BORROWER_LLC_CERTIFICATE_OF_GOOD_STANDING, FOREIGN_ENTITY_REGISTRATION, RECORDED_UCC1_STATE, RECORDED_UCC1_COUNTY, SELLER_ENTITY_DOCUMENTS, CERT_OF_FORMATION_OR_INC, CERT_OF_GOOD_STANDING_OR_EXISTENCE, OPERATING_AGREEMENT_BYLAWS_STOCK, RECORDED_CEMA, RECORDED_BUILDING_MORTGAGE } from "@roc/feature-documents";
import { DocReviewInternalService } from "../service/docReviewInternalService";
import { HudFormStore } from "../components/genericDocReview/attorneyHud/hudFormStore";
import { EntityReviewFormStore } from "../components/genericDocReview/borrowerEntity/entityReviewFormStore";
import { PropertyTaxesFormStore } from "../components/genericDocReview/propertyTaxes/propertyTaxesFormStore";
import { UwHudFormStore } from "../components/genericDocReview/uwHud/uwHudFormStore";
import { HoaFormStore } from "../components/genericDocReview/hoa/hoaFormStore";
import { PurchaseContractFormStore } from "../components/genericDocReview/purchaseContract/purchaseContractFormStore";
import { LicenseFormStore } from '../components/genericDocReview/license/licenseFormStore';
import { WholesalerContractFormStore } from "../components/genericDocReview/wholesalerContract/wholesalerContractFormStore";
import { Document } from "@roc/feature-types";

const processingStatusText: Record<string, string> = {
  IN_PROGRESS: 'Processing - This may take a few seconds. Click Refresh to check for results.',
  SUCCESS: 'Documents ready — all uploaded files have been processed',
  FAILED: 'Processing failed — please try again',
  FLAGGED: 'Flagged for review — please check the extracted data',
};

export class DocReviewStoreV2 {
  globalStore: GlobalStore;
  processingStatus: string | null = null;
  errorMessage: string | null = null;
  docReviewData: Record<string, any> = {};
  document: Document | null = null;
  private service: DocReviewInternalService;
  formStoreMap: Record<string, any>;

  //custom review forms
  entityReviewFormStore: EntityReviewFormStore;
  hudFormStore: HudFormStore;
  propertyTaxesFormStore: PropertyTaxesFormStore;
  uwHudFormStore: UwHudFormStore;
  hoaFormStore: HoaFormStore;
  purchaseContractFormStore: PurchaseContractFormStore;
  licenseFormStore: LicenseFormStore;
  wholesalerContractFormStore: WholesalerContractFormStore;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.service = new DocReviewInternalService();
    this.entityReviewFormStore = new EntityReviewFormStore(globalStore);
    this.hudFormStore = new HudFormStore(globalStore);
    this.propertyTaxesFormStore = new PropertyTaxesFormStore(globalStore);
    this.uwHudFormStore = new UwHudFormStore(globalStore);
    this.hoaFormStore = new HoaFormStore(globalStore);
    this.purchaseContractFormStore = new PurchaseContractFormStore(globalStore);
    this.licenseFormStore = new LicenseFormStore(globalStore);
    this.wholesalerContractFormStore = new WholesalerContractFormStore(globalStore);
    this.formStoreMap = {
      [HUD]: this.hudFormStore,
      [EIN_LETTER]: this.entityReviewFormStore,
      [TAX_CERTIFICATE]: this.propertyTaxesFormStore,
      [UW_HUD]: this.uwHudFormStore,
      [HUD_DOC]: this.uwHudFormStore,
      [HOA]: this.hoaFormStore,
      [PURCHASE_CONTRACT]: this.purchaseContractFormStore,
      [GC_LICENSE]: this.licenseFormStore,
      [BROKER_LICENSE]: this.licenseFormStore,
      [WHOLESALER_CONTRACT]: this.wholesalerContractFormStore,
      [CERTIFICATE_OF_FORMATION]: this.entityReviewFormStore,
      [ARTICLES_OF_INCORPORATION]: this.entityReviewFormStore,
      [CERTIFICATE_OF_GOOD_STANDING]: this.entityReviewFormStore,
      [CERTIFICATE_OF_GOOD_STANDING_BASE]: this.entityReviewFormStore,
      [CERTIFICATE_OF_EXISTENCE]: this.entityReviewFormStore,
      [BORROWER_LLC_CERTIFICATE_OF_GOOD_STANDING]: this.entityReviewFormStore,
      [OPERATING_AGREEMENT]: this.entityReviewFormStore,
      [BYLAWS]: this.entityReviewFormStore,
      [BORROWING_CORP_STOCK_CERTIFICATE]: this.entityReviewFormStore,
      [FOREIGN_ENTITY_REGISTRATION]: this.entityReviewFormStore,
      [CERT_OF_FORMATION_OR_INC]: this.entityReviewFormStore,
      [CERT_OF_GOOD_STANDING_OR_EXISTENCE]: this.entityReviewFormStore,
      [OPERATING_AGREEMENT_BYLAWS_STOCK]: this.entityReviewFormStore,
    };
    makeObservable(this, {
      processingStatus: observable,
      errorMessage: observable,
      docReviewData: observable,
      document: observable,
      setProcessingStatus: action,
      setDocReviewData: action,
      setDocument: action,
      reset: action,
      getReviewData: flow,
      getReviewTaskData: flow,
      saveDocReviewTaskData: flow,
      loadEntityEditForm: flow,
      statusText: computed,
      canShowEditButton: action,
    });
  }

  canShowEditButton(document: Document): boolean {
    if (this.getIsEditDisabled(document?.taskName || '')) {
      return false;
    }
    if (document?.taskName === TAX_CERTIFICATE || document?.taskName === HUD) {
      return true;
    }
    return !!document?.dropboxPath;
  }

  get statusText(): string {
    if (!this.globalStore.isInternalPortal) {
      return 'Processing - Your document is being processed. This usually takes a few seconds.';
    }
    const hasErrorMessage =
      (this.processingStatus === 'FAILED' || this.processingStatus === 'FLAGGED') &&
      this.errorMessage;
    return hasErrorMessage
      ? this.errorMessage
      : processingStatusText[this.processingStatus] ?? '';
  }

  setProcessingStatus(status: string | null) {
    this.processingStatus = status;
  }

  setDocReviewData(data: Record<string, any>) {
    this.docReviewData = data;
  }

  setDocument(document: Document | null) {
    this.document = document;
  }

  getIsEditDisabled(taskName: string) {
    return [
      PROOF_OF_RECEIPT,
      CLOSING_INSTRUCTION_LETTER,
      CLOSING_PROTECTION_LETTER,
      FINAL_TITLE_POLICY,
      RECORDED_MORTGAGE,
      RECORDED_ALR,
      FILING_RECEIPT,
      RECORDED_UCC1_STATE,
      RECORDED_UCC1_COUNTY,
      SELLER_ENTITY_DOCUMENTS,
      RECORDED_CEMA,
      RECORDED_BUILDING_MORTGAGE
    ].includes(taskName);
  }

  reset() {
    this.processingStatus = null;
    this.errorMessage = null;
    this.docReviewData = {};
    this.document = null;
  }

  *getReviewTaskData(taskId: number) {
    try {
      const response: ApiResponse = yield this.service.getTaskById(taskId);
      const raw = response?.data?.data ?? null;
      this.document = raw ? {
        ...raw,
        taskName: raw.taskCode,
        dropboxPath: raw.path,
        collateralId: raw.typeId2
      } : null;
      if (this.document?.taskId) {
        yield this.getReviewData(this.document.taskId);
      }
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while fetching the task.',
      });
    }
  }

  *saveDocReviewTaskData(document: Document) {
    if (!this.formStoreMap[document?.taskName]?.runFormValidationWithMessage()) {
      throw new Error();
    }

    try {
      const formValues = this.formStoreMap[document?.taskName].getFormValues();
      yield this.service.saveDocReviewTaskData(document?.taskId, formValues);
      yield this.getReviewData(document?.taskId);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error saving review task data',
      });
      throw new Error();
    }
  }

  *loadEntityEditForm() {
    this.entityReviewFormStore.reset();
    let editForm = {};
    try {
      const response: ApiResponse = yield this.service.getBorrowerEntityEditForm(
        this.document?.taskId
      );
      editForm = response?.data?.data ?? {};
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error getting the borrower entity details for this loan',
      });
    }
    this.entityReviewFormStore.load(
      this.docReviewData,
      this.document?.taskName ?? '',
      editForm
    );
  }

  *getReviewData(taskId: number) {
    try {
      const response: ApiResponse = yield this.service.getReviewTaskData(taskId);
      this.docReviewData = response?.data?.data?.docReviewData ? JSON.parse(response?.data?.data?.docReviewData) : {};
      this.processingStatus = response?.data?.data?.processingStatus ?? null;
      this.errorMessage = response?.data?.data?.errorMessage ?? null;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error getting review task data',
      });
    }
  }
}
